import type { PayloadAction } from '@reduxjs/toolkit'

import type {
  BottomDropdownState,
  DataItem,
  EndpointConfig,
  OperatorOption,
  PickerSliceState,
} from './types'
import {
  createInitialBottomState,
  createInitialCanonicalLevel,
  DEFAULT_GROUP_ID,
  ensurePicker,
  getGroupConfigForPicker,
} from './types'

// ═══════════════════════════════════════════════════════════════════════════
// Reducer Cases
// ═══════════════════════════════════════════════════════════════════════════

export const bottomReducers = {
  loadBottomData: (
    state: PickerSliceState,
    action: PayloadAction<{
      pickerId: number
      groupId?: string
      data: DataItem[]
      totalCount?: number
      pageSize?: number
    }>
  ) => {
    const {
      pickerId,
      groupId = DEFAULT_GROUP_ID,
      data,
      totalCount,
      pageSize,
    } = action.payload
    const picker = ensurePicker(state, pickerId, groupId)
    const dataHash: Record<string, DataItem> = {}
    for (const item of data) {
      dataHash[item.id] = item
    }
    picker.bottom.data = dataHash
    // Refresh labels for already-selected items when richer data arrives. Items
    // preselected from the AI payload carry only id+name; once the options
    // endpoint responds (e.g. with a nameless location's street-address label),
    // adopt it so the trigger/summary doesn't keep showing the raw id.
    for (const key of picker.bottom.selectedItems) {
      if (dataHash[key]) {
        picker.bottom.selectedItemsData[key] = dataHash[key]
      }
    }
    if (totalCount !== undefined) {
      picker.bottom.totalCount = totalCount
    }
    if (pageSize !== undefined) {
      picker.bottom.pageSize = pageSize
    }
  },

  setBottomSearchTerm: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; term: string }>
  ) => {
    const { pickerId, term } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.bottom.searchTerm = term
      picker.bottom.page = 1
    }
  },

  setBottomPage: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; page: number }>
  ) => {
    const { pickerId, page } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.bottom.page = page
    }
  },

  setBottomTotalCount: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; totalCount: number }>
  ) => {
    const { pickerId, totalCount } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.bottom.totalCount = totalCount
    }
  },

  addBottomSelectedItem: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; key: string }>
  ) => {
    const { pickerId, key } = action.payload
    const picker = state.pickers[pickerId]
    if (picker && !picker.bottom.selectedItems.includes(key)) {
      picker.bottom.selectedItems.push(key)
      // Store the item data so we can display it later
      const item = picker.bottom.data[key]
      if (item) {
        picker.bottom.selectedItemsData[key] = item
      }
    }
  },

  removeBottomSelectedItem: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; key: string }>
  ) => {
    const { pickerId, key } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.bottom.selectedItems = picker.bottom.selectedItems.filter(
        k => k !== key
      )
      // Remove from selectedItemsData
      delete picker.bottom.selectedItemsData[key]
      // Auto-clear showingSelections when no items remain
      if (picker.bottom.selectedItems.length === 0) {
        picker.bottom.showingSelections = false
      }
    }
  },

  setBottomSelectedItems: (
    state: PickerSliceState,
    action: PayloadAction<{
      pickerId: number
      groupId?: string
      keys: string[]
    }>
  ) => {
    const { pickerId, groupId = DEFAULT_GROUP_ID, keys } = action.payload
    const picker = ensurePicker(state, pickerId, groupId)
    picker.bottom.selectedItems = keys
    // Rebuild selectedItemsData from current data
    const newSelectedItemsData: Record<string, DataItem> = {}
    for (const key of keys) {
      const item =
        picker.bottom.data[key] || picker.bottom.selectedItemsData[key]
      if (item) {
        newSelectedItemsData[key] = item
      }
    }
    picker.bottom.selectedItemsData = newSelectedItemsData
  },

  clearBottomSelection: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number }>
  ) => {
    const { pickerId } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.bottom.selectedItems = []
      picker.bottom.selectedItemsData = {}
      picker.bottom.showingSelections = false
    }
  },

  /**
   * Select the given keys (the currently-visible "page"). The caller supplies
   * the keys so the set honors any active search filter (see
   * filterOptionsBySearch) rather than the raw, unfiltered data hash.
   */
  selectAllBottomOnPage: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; keys: string[] }>
  ) => {
    const { pickerId, keys } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      for (const key of keys) {
        if (!picker.bottom.selectedItems.includes(key)) {
          picker.bottom.selectedItems.push(key)
          const item = picker.bottom.data[key]
          if (item) {
            picker.bottom.selectedItemsData[key] = item
          }
        }
      }
    }
  },

  /** Deselect the given keys (the currently-visible, filter-aware "page"). */
  deselectAllBottomOnPage: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; keys: string[] }>
  ) => {
    const { pickerId, keys } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      const pageKeys = new Set(keys)
      picker.bottom.selectedItems = picker.bottom.selectedItems.filter(
        key => !pageKeys.has(key)
      )
      // Remove from selectedItemsData
      pageKeys.forEach(key => {
        delete picker.bottom.selectedItemsData[key]
      })
      // Auto-clear showingSelections when no items remain
      if (picker.bottom.selectedItems.length === 0) {
        picker.bottom.showingSelections = false
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Configuration Actions
  // ═══════════════════════════════════════════════════════════════════════════

  setBottomLabel: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; label: string }>
  ) => {
    const { pickerId, label } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.bottom.label = label
    }
  },

  setBottomOperator: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; operator: string }>
  ) => {
    const { pickerId, operator } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.bottom.operator = operator
      // An operator that picks no values hides a canonical level 2, so its picks go with it
      const option = getGroupConfigForPicker(
        state,
        pickerId
      )?.level2.operatorOptions.find(({ id }) => id === operator)
      if (option?.requiresValues === false && picker.canonical) {
        picker.canonical.level2 = createInitialCanonicalLevel()
      }
    }
  },

  setBottomOperatorOptions: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; options: OperatorOption[] }>
  ) => {
    const { pickerId, options } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.bottom.operatorOptions = options
    }
  },

  setBottomEndpointConfig: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; config: EndpointConfig }>
  ) => {
    const { pickerId, config } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.bottom.endpointConfig = config
    }
  },

  setBottomSubtitle: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; subtitle: string | null }>
  ) => {
    const { pickerId, subtitle } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.bottom.subtitle = subtitle
    }
  },

  toggleBottomShowingSelections: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number }>
  ) => {
    const { pickerId } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.bottom.showingSelections = !picker.bottom.showingSelections
    }
  },

  setBottomShowingSelections: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; showingSelections: boolean }>
  ) => {
    const { pickerId, showingSelections } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.bottom.showingSelections = showingSelections
    }
  },

  setBottomDisabled: (
    state: PickerSliceState,
    action: PayloadAction<{
      pickerId: number
      groupId?: string
      disabled: boolean
    }>
  ) => {
    const { pickerId, groupId = DEFAULT_GROUP_ID, disabled } = action.payload
    const picker = ensurePicker(state, pickerId, groupId)
    picker.bottom.disabled = disabled
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Open/Close Actions
  // ═══════════════════════════════════════════════════════════════════════════

  setBottomIsOpen: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number; isOpen: boolean }>
  ) => {
    const { pickerId, isOpen } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.bottom.isOpen = isOpen
    }
  },

  toggleBottomIsOpen: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number }>
  ) => {
    const { pickerId } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.bottom.isOpen = !picker.bottom.isOpen
    }
  },

  openBottom: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number }>
  ) => {
    const { pickerId } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.bottom.isOpen = true
    }
  },

  closeBottom: (
    state: PickerSliceState,
    action: PayloadAction<{ pickerId: number }>
  ) => {
    const { pickerId } = action.payload
    const picker = state.pickers[pickerId]
    if (picker) {
      picker.bottom.isOpen = false
      // Reset search term and showing selections mode when closing
      picker.bottom.searchTerm = ''
      picker.bottom.showingSelections = false
    }
  },
}

// ═══════════════════════════════════════════════════════════════════════════
// Selectors (flat structure - only need pickerId)
// ═══════════════════════════════════════════════════════════════════════════

type RootState = { picker: PickerSliceState }

const defaultBottomState = createInitialBottomState()

export const selectBottom = (
  state: RootState,
  pickerId: number
): BottomDropdownState =>
  state.picker.pickers[pickerId]?.bottom ?? defaultBottomState

export const selectBottomData = (
  state: RootState,
  pickerId: number
): Record<string, DataItem> => state.picker.pickers[pickerId]?.bottom.data ?? {}

export const selectBottomSelectedItems = (
  state: RootState,
  pickerId: number
): string[] => state.picker.pickers[pickerId]?.bottom.selectedItems ?? []

export const selectBottomSelectedItemsCount = (
  state: RootState,
  pickerId: number
): number => state.picker.pickers[pickerId]?.bottom.selectedItems.length ?? 0

export const selectBottomSelectedItemsData = (
  state: RootState,
  pickerId: number
): Record<string, DataItem> =>
  state.picker.pickers[pickerId]?.bottom.selectedItemsData ?? {}

export const selectBottomSearchTerm = (
  state: RootState,
  pickerId: number
): string => state.picker.pickers[pickerId]?.bottom.searchTerm ?? ''

export const selectBottomPage = (state: RootState, pickerId: number): number =>
  state.picker.pickers[pickerId]?.bottom.page ?? 1

export const selectBottomTotalCount = (
  state: RootState,
  pickerId: number
): number => state.picker.pickers[pickerId]?.bottom.totalCount ?? 0

export const selectBottomPageSize = (
  state: RootState,
  pickerId: number
): number => state.picker.pickers[pickerId]?.bottom.pageSize ?? 50

export const selectBottomLabel = (state: RootState, pickerId: number): string =>
  state.picker.pickers[pickerId]?.bottom.label ?? ''

export const selectBottomOperator = (
  state: RootState,
  pickerId: number
): string => state.picker.pickers[pickerId]?.bottom.operator ?? 'any'

export const selectBottomOperatorOptions = (
  state: RootState,
  pickerId: number
): OperatorOption[] =>
  state.picker.pickers[pickerId]?.bottom.operatorOptions ?? []

export const selectBottomEndpointConfig = (
  state: RootState,
  pickerId: number
): EndpointConfig | null =>
  state.picker.pickers[pickerId]?.bottom.endpointConfig ?? null

export const selectBottomSubtitle = (
  state: RootState,
  pickerId: number
): string | null => state.picker.pickers[pickerId]?.bottom.subtitle ?? null

export const selectBottomShowingSelections = (
  state: RootState,
  pickerId: number
): boolean => state.picker.pickers[pickerId]?.bottom.showingSelections ?? false

export const selectBottomDisabled = (
  state: RootState,
  pickerId: number
): boolean => state.picker.pickers[pickerId]?.bottom.disabled ?? true

export const selectBottomIsOpen = (
  state: RootState,
  pickerId: number
): boolean => state.picker.pickers[pickerId]?.bottom.isOpen ?? false
